// client/src/components/logistics/AddLicenseModalMui.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Divider,
  InputAdornment,
  useMediaQuery,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";

import { useGetLicenseRecipientsQuery } from "../../redux/logistics/logisticsApi";

const AddLicenseModalMui = ({ onClose, onSubmit, open = true }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const currentUser = useSelector((state) => state.auth?.user || state.auth?.currentUser || null);
  const currentRole = String(currentUser?.role || "").trim().toLowerCase();

  const { data: recipients = [], isLoading } = useGetLicenseRecipientsQuery();

  const recipientOptions = useMemo(() => {
    if (!Array.isArray(recipients)) return [];
    return recipients;
  }, [recipients]);

  const [formData, setFormData] = useState({
    dealer: "",
    unitAmount: 100,
    currency: "INR",
    count: 0,
    tax: 18,
    status: "Completed",
    paymentAmount: 0,
    paymentDate: "",
    type: "Yearly",
    remarks: "",
    transactionType: "Sell",
    total: 0,
  });

  const [error, setError] = useState("");

  // ✅ AUTO TOTAL CALCULATION
  useEffect(() => {
    const baseTotal = Number(formData.unitAmount || 0) * Number(formData.count || 0);
    const taxAmount = (baseTotal * Number(formData.tax || 0)) / 100;
    const finalTotal = baseTotal + taxAmount;

    setFormData((prev) => ({
      ...prev,
      total: finalTotal,
      paymentAmount: finalTotal,
    }));
  }, [formData.unitAmount, formData.count, formData.tax]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // numeric fields
    if (["unitAmount", "count", "tax"].includes(name)) {
      setFormData((prev) => ({ ...prev, [name]: Number(value) }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    setError("");

    if (!formData.dealer) return setError("Please select recipient");
    if (!formData.count || formData.count <= 0) return setError("Count must be greater than 0");
    if (!formData.paymentDate) return setError("Payment Date is required");

    onSubmit(formData);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 3,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item>
            <Typography variant="h6" fontWeight={700}>
              Purchase License
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {["admin", "manager", "office_emp"].includes(currentRole)
                ? "Assign license count to Admin or Dealer"
                : "Assign license count to a dealer"}
            </Typography>
          </Grid>
          <Grid item>
            <IconButton onClick={onClose} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        <Grid container spacing={2}>
          {/* Recipient */}
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Recipient *"
              name="dealer"
              value={formData.dealer}
              onChange={handleChange}
              disabled={isLoading}
            >
              <MenuItem value="">
                {isLoading ? "Loading recipients..." : "-- Select Recipient --"}
              </MenuItem>
              {recipientOptions.map((item) => (
                <MenuItem key={item._id} value={item._id}>
                  {item.username} ({String(item.role || "").replace("_", " ")})
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Unit Amount */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Single Unit Amount"
              name="unitAmount"
              value={formData.unitAmount}
              onChange={handleChange}
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              }}
            />
          </Grid>

          {/* Currency */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Currency"
              value="INR"
              InputProps={{ readOnly: true }}
            />
          </Grid>

          {/* Count */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Count *"
              name="count"
              value={formData.count}
              onChange={handleChange}
              helperText="Each count behaves like 1 license slot."
            />
          </Grid>

          {/* Tax */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Tax (%)"
              name="tax"
              value={formData.tax}
              onChange={handleChange}
            />
          </Grid>

          {/* Total */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Total"
              value={formData.total}
              InputProps={{
                readOnly: true,
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              }}
            />
          </Grid>

          {/* Status */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Status"
              name="status"
              value={formData.status}
              onChange={handleChange}
            />
          </Grid>

          {/* Payment Amount */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Payment Amount"
              value={formData.paymentAmount}
              InputProps={{
                readOnly: true,
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              }}
            />
          </Grid>

          {/* Payment Date */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="date"
              label="Payment Date *"
              name="paymentDate"
              value={formData.paymentDate}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Type */}
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Type"
              name="type"
              value={formData.type}
              onChange={handleChange}
            >
              <MenuItem value="Yearly">Yearly</MenuItem>
              <MenuItem value="Monthly">Monthly</MenuItem>
              <MenuItem value="Daily">Daily</MenuItem>
            </TextField>
          </Grid>

          {/* Remarks */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Remarks"
              name="remarks"
              value={formData.remarks}
              onChange={handleChange}
            />
          </Grid>

          {/* Transaction Type */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Transaction Type"
              value="Sell"
              InputProps={{ readOnly: true }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddLicenseModalMui;
